import React, { ReactNode, useState, useMemo, useCallback } from 'react';
import { 
  Navbar, 
  Button, 
  Alignment, 
  NavbarHeading, 
  NavbarDivider, 
  NavbarGroup,
  Menu,
  MenuItem,
  Popover,
  Icon,
  Tag,
  Text
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import AvatarPresenceIndicatorFixed from '../presence/AvatarPresenceIndicatorFixed';
import { HealthIndicator } from '../HealthIndicator';
import { observer } from 'mobx-react-lite';
import { featureFlags } from '../../featureFlags'; // Import feature flags
// FeedbackDialog removed - feedback is handled by Raft widget at bottom-left
import { TicketType } from '../../stores/TicketStore';

// App version
const APP_VERSION = '0.2.0';

interface InstantNavbarProps {
  rightElement?: ReactNode;
}

/**
 * Navbar component for the application header
 * Provides navigation, branding, and action buttons
 * Uses InstantDB for auth and presence, but local MobX for theme
 */
const InstantNavbar: React.FC<InstantNavbarProps> = observer(({ rightElement }) => {
  const appStore = useAppStore();
  const { auth: authStore, logs: logsStore, layout: layoutStore, theme: themeStore } = appStore;
  const user = authStore.user;
  const stytch = useStytchB2BClient();
  
  // Get user email directly from auth store
  const userEmail = useMemo(() => {
    return user?.email || '';
  }, [user]);
  
  // Get user name from auth store
  const userName = useMemo(() => {
    return user?.name || userEmail || 'User';
  }, [user, userEmail]);
  
  // Helper to capitalize first letter
  const capitalizeFirstLetter = (str: string): string => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Derive displayName using useMemo - use name from auth store
  const displayName = useMemo(() => {
    if (user?.name) {
      return user.name;
    } else if (user?.email) {
      // Extract name from email if no name provided
      const emailParts = user.email.split('@');
      const nameParts = emailParts[0].split(/[._-]/);
      if (nameParts.length > 1) {
        return nameParts.map(part => capitalizeFirstLetter(part)).join(' ');
      }
      return capitalizeFirstLetter(emailParts[0]);
    }
    return 'User';
  }, [user]);

  // Generate avatar URL from email
  const generateAvatarUrl = (email: string) => {
    if (!email) return 'https://ui-avatars.com/api/?name=U&background=random&color=fff';
    
    // Create a hash from email for consistent colors
    const hash = Array.from(email)
      .reduce((hash, char) => char.charCodeAt(0) + ((hash << 5) - hash), 0);
    const color = Math.abs(hash % 360);
    
    // Get name from email/ID for the avatar
    let initials = '';
    
    // Check if it's an email
    if (email.includes('@')) {
      const emailParts = email.split('@');
      const nameParts = emailParts[0].split(/[._-]/);
      
      if (nameParts.length > 1) {
        initials = `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
      } else {
        initials = nameParts[0].charAt(0).toUpperCase();
      }
    } else {
      // It's not an email, treat as an ID with possible separators
      const nameParts = email.split(/[._-]/);
      
      if (nameParts.length > 1) {
        initials = `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
      } else {
        initials = nameParts[0].charAt(0).toUpperCase();
      }
    }
    
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${color.toString(16).padStart(6, '0')}&color=fff`;
  };

  // Create the authorization URL for Google login
  const createGoogleAuthUrl = () => {
    // Return login URL instead of redirecting immediately
    return '/login';
    // Old InstantDB code:
    // return db.auth.createAuthorizationURL({
    //   clientName: "google",
    //   redirectURL: window.location.href,
    // });
  };
  
  // Format email/ID for display - MOVED HERE TO FIX REFERENCE ERROR
  const formatUserEmail = (email: string) => {
    if (!email) return '';
    // Check if it's an email
    if (email.includes('@')) {
      return email;
    }
    // Try to format it nicely if it's not a typical email
    const parts = email.split(/[._-]/);
    if (parts.length > 1) {
      return parts.map(p => capitalizeFirstLetter(p)).join(' ');
    }
    return email;
  };

  // Feedback is handled by the Raft widget at bottom-left, no need for duplicate buttons

  const helpMenu = (
    <Menu>
      <MenuItem 
        icon={IconNames.CONSOLE} 
        text="Center Debug Panel"
        onClick={() => {
          // Center the debug panel
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          const panelWidth = 300; // Default debug panel width
          const panelHeight = 400; // Approximate debug panel height
          
          // Calculate center position
          const x = (viewportWidth - panelWidth) / 2;
          const y = (viewportHeight - panelHeight) / 2;
          
          // Update debug panel position
          logsStore.setDebugControlsPosition({ x, y });
        }}
      />
      <MenuItem 
        icon={layoutStore.isDebugPanelVisible ? IconNames.EYE_OFF : IconNames.EYE_OPEN}
        text={layoutStore.isDebugPanelVisible ? "Hide Debug Panel" : "Show Debug Panel"}
        onClick={() => {
          layoutStore.toggleDebugPanel();
        }}
      />
    </Menu>
  );

  // Handle sign out with Stytch session revoke
  const handleSignOut = useCallback(async () => {
    try {
      console.log('🚪 [InstantNavbar] Starting sign out...');
      
      // 1. Set flag to prevent auto-redirect on login page
      sessionStorage.setItem('just_signed_out', 'true');
      
      // 2. Revoke Stytch session first (clears Stytch cookies and session)
      await stytch.session.revoke();
      console.log('✅ [InstantNavbar] Stytch session revoked');
      
      // 3. Clear our backend session and local state
      await authStore.signOut();
      console.log('✅ [InstantNavbar] Backend session cleared');
      
      // 4. Destroy the AppStore completely
      const { destroyAppStore } = await import('../../stores/AppStore');
      destroyAppStore();
      console.log('✅ [InstantNavbar] AppStore destroyed');
      
      // 5. Clear all localStorage (Stytch data + any cached data)
      localStorage.clear();
      console.log('✅ [InstantNavbar] LocalStorage cleared');
      
      // 6. Redirect to login (this will reload the page with fresh state)
      window.location.href = '/login';
    } catch (error) {
      console.error('❌ [InstantNavbar] Sign out error:', error);
      // Force cleanup even if errors occur
      sessionStorage.setItem('just_signed_out', 'true');
      localStorage.clear();
      window.location.href = '/login';
    }
  }, [stytch, authStore]);

  // Memoize the user menu
  const userMenu = useMemo(() => {
    if (!user) {
      return (
        <Menu>
          <MenuItem text="Not signed in" disabled />
        </Menu>
      );
    }

    return (
      <Menu className="user-menu">
        <div className="user-menu-header">
          <img 
            src={user.avatar || generateAvatarUrl(user.email || user.id)} 
            alt={displayName} 
            className="user-menu-avatar" 
          />
          <div className="user-menu-info">
            <div className="user-menu-name">{displayName}</div>
            <div className="user-menu-email">
              {userEmail || "Email not available"}
            </div>
          </div>
        </div>
        <MenuItem icon={IconNames.LOG_OUT} text="Sign Out" onClick={handleSignOut} />
      </Menu>
    );
  }, [user, displayName, userEmail, handleSignOut]);

  return (
    <>
      <Navbar className="app-navbar">
        <NavbarGroup align={Alignment.LEFT}>
          <NavbarHeading>Forethought</NavbarHeading>
          <Tag minimal>v{APP_VERSION}</Tag>
          <NavbarDivider />
          {/* Horizontal avatar stack showing online users */}
          {user && featureFlags.ENABLE_PRESENCE_INDICATOR && (
            <AvatarPresenceIndicatorFixed userId={user.id} />
          )}
        </NavbarGroup>
        <NavbarGroup align={Alignment.RIGHT}>
          {user ? (
            <></>
          ) : null}
          <NavbarDivider />
          
          {rightElement && (
            <>
              {rightElement}
              <NavbarDivider />
            </>
          )}
          
          <Button
            icon={
              themeStore.theme === 'light' ? IconNames.MOON :
              themeStore.theme === 'dark' ? IconNames.FLASH :
              IconNames.CONTRAST
            }
            text={
              themeStore.theme === 'light' ? 'Light' :
              themeStore.theme === 'dark' ? 'Dark' :
              'Auto'
            }
            minimal
            onClick={() => themeStore.toggleTheme()}
            title="Toggle theme (Light → Dark → Auto)"
          />
          <NavbarDivider />
          <Popover content={helpMenu} position="bottom">
            <Button icon={IconNames.LIFESAVER} minimal />
          </Popover>
          <NavbarDivider />
          
          {user ? (
            <Popover content={userMenu} position="bottom-right">
              <Button 
                minimal
                className="user-profile-button"
                title={displayName}
              >
                {user.avatar || user.email ? (
                  <img 
                    src={user.avatar || generateAvatarUrl(user.email || user.id)} 
                    alt={displayName} 
                    className="navbar-avatar" 
                  />
                ) : (
                  <Icon icon={IconNames.USER} />
                )}
              </Button>
            </Popover>
          ) : (
            <a href={createGoogleAuthUrl()} title="Sign in to your account">
              <Button minimal icon={IconNames.LOG_IN} text="Sign In" />
            </a>
          )}
        </NavbarGroup>
      </Navbar>
    </>
  );
});

export default InstantNavbar; 